import React, { useContext, useEffect, useState } from "react";
import { Grid, Card, MenuItem, Typography, CircularProgress, Button } from "@mui/material";
import AuthContext from "context/auth_context";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { useNavigate } from "react-router-dom";
import { format, addDays } from "date-fns";

const MultipleMessage = () => {
  const [categories, setCategories] = useState([]);
  const [channels, setChannels] = useState([]);
  const [formData, setFormData] = useState({
    channel: "",
    category: "",
    publish_date: "",
    message_type: "multiple_products",
    uploaded_files: [], // Store base64 encoded files with names
    message_texts: [
      {
        text: "",
        discount: "",
        deal_price: "",
        link: "",
        currency: "₹",
        sub_id: "",
      },
    ],
  });
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const tokens = JSON.parse(localStorage.getItem("authTokens"));
  const access_token = tokens?.access;

  const getEgyptDate = () => {
    const egyptDate = new Date(
      new Intl.DateTimeFormat("en-US", { timeZone: "Africa/Cairo" }).format(new Date())
    );
    return egyptDate;
  };
  const today = getEgyptDate();
  const tomorrow = addDays(today, 1);
  // const formatDate = (date) => date.toISOString().split("T")[0];
  const formatDate = (date) => format(date, "yyyy-MM-dd", { timeZone: "Africa/Cairo" });

  const { signOut } = useContext(AuthContext);
  const navigate = useNavigate();

  const fetchData = async (url, isChannelData) => {
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${access_token}`,
        },
      });

      if (response.status === 401) {
        signOut();
        navigate("/sign-out");
      }

      const data = await response.json();
      if (isChannelData) {
        return data?.data?.length ? data["data"] : null; // Check if 'data.data' exists and has length
      } else {
        return data?.length ? data : null; // Check if data exists and has length
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      return null;
    }
  };

  useEffect(() => {
    const fetchCategoriesAndChannels = async () => {
      const categoriesData = await fetchData(
        "https://teleback.info/api_admin/pups/category/list/",
        false
      );
      const channelsData = await fetchData(
        "https://teleback.info/api_admin/pups/telegramchannels/",
        true
      );
      if (categoriesData) setCategories(categoriesData);
      // console.log(channelsData);
      if (channelsData) setChannels(channelsData);
    };
    fetchCategoriesAndChannels();
  }, [access_token]);

  const handleChange = (e, index) => {
    const { name, value, files } = e.target;
    if (name === "uploaded_files" && files.length > 0) {
      convertFilesToBase64(files).then((base64Files) => {
        setFormData((prevData) => ({
          ...prevData,
          uploaded_files: base64Files, // Store each file as { name, content }
        }));
      });
    } else if (name === "channel" || name === "category") {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    } else {
      const updatedMessages = [...formData.message_texts];
      updatedMessages[index][name] = value;
      setFormData({ ...formData, message_texts: updatedMessages });
    }
  };

  const handleAddMessage = () => {
    setFormData((prevData) => ({
      ...prevData,
      message_texts: [
        ...prevData.message_texts,
        { text: "", discount: "", deal_price: "", link: "", currency: "₹", sub_id: "" },
      ],
    }));
  };

  const handleRemoveMessage = (index) => {
    const updatedMessages = formData.message_texts.filter((_, idx) => idx !== index);
    setFormData({ ...formData, message_texts: updatedMessages });
  };

  const convertFilesToBase64 = (files) => {
    return Promise.all(
      Array.from(files).map((file) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            resolve({ name: file.name, content: reader.result.split(",")[1] });
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      })
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Check if Channel, Category, and Publish Date are provided
    if (!formData.channel || !formData.category || !formData.publish_date) {
      setErrorMessage("Channel, Category, and Publish Date... are required.");
      return;
    }

    if (formData.message_texts.length === 0) {
      setErrorMessage("At least one Message Text is Required.");
      return;
    }

    if (formData.uploaded_files.length === 0) {
      setErrorMessage("At least one File is Required.");
      return;
    }

    // Validate that message_texts is not empty and all its fields are filled
    const invalidMessages = formData.message_texts.filter(
      (msg) =>
        !msg.text ||
        !msg.discount ||
        !msg.deal_price ||
        !msg.link || // You can add any other required fields here
        !msg.sub_id
    );
    if (invalidMessages.length > 0) {
      setErrorMessage(
        "All fields in message texts (text, discount, deal price, link, sub_id...) are required."
      );
      return;
    }

    setErrorMessage("");
    setIsSubmitting(true);

    const messageData = {
      channel: formData.channel,
      category: formData.category,
      publish_date: formData.publish_date,
      message_type: "multiple_products",
      message_texts: formData.message_texts,
      uploaded_files: formData.uploaded_files,
    };

    try {
      const response = await fetch("https://teleback.info/api_admin/pups/telegrammessage/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${access_token}`,
        },
        body: JSON.stringify(messageData),
      });

      setIsSubmitting(false);
      if (response.status === 401) {
        signOut();
        navigate("/sign-out");
      }
      if (response.ok) {
        // console.log("Messages created successfully");
        navigate("/messages");
      } else {
        setErrorMessage("Failed to create messages");
      }
    } catch (error) {
      setIsSubmitting(false);
      console.error("Error submitting message:", error);
      setErrorMessage("Error submitting message. Please try again.");
    }
  };

  const renderSelectField = (label, name, options, selectedValue) => (
    <MDBox mb={2}>
      <MDInput
        select
        label={label}
        name={name}
        value={selectedValue || ""}
        variant="standard"
        fullWidth
        onChange={(e) => handleChange(e)}
      >
        {options.map((option) => (
          <MenuItem key={option.pk || option.id} value={option.pk || option.id}>
            {option.title || option.name}
          </MenuItem>
        ))}
      </MDInput>
    </MDBox>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Grid container justifyContent="center" spacing={0}>
        <Grid item xs={12} md={8} lg={6}>
          <MDBox mb={3} mt={3}>
            <Card>
              <MDBox
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="success"
                mx={2}
                mt={-3}
                p={3}
                mb={1}
                textAlign="center"
              >
                <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
                  Create Multiple Messages
                </MDTypography>
              </MDBox>
              <MDBox pt={4} pb={3} px={3}>
                <MDBox component="form" role="form" onSubmit={handleSubmit}>
                  {errorMessage && (
                    <MDBox mb={2}>
                      <MDTypography color="error">{errorMessage}</MDTypography>
                    </MDBox>
                  )}
                  {/* Main Message Fields (channel, category, publish_date) */}
                  <MDBox mb={2}>
                    <MDInput
                      type="date"
                      label="Publish Date"
                      name="publish_date"
                      value={formData.publish_date}
                      variant="standard"
                      fullWidth
                      error={!formData.publish_date}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        min: formatDate(today),
                        max: formatDate(tomorrow),
                      }}
                      onChange={(e) => setFormData({ ...formData, publish_date: e.target.value })}
                    />
                  </MDBox>
                  {renderSelectField("Channel", "channel", channels, formData.channel)}
                  {renderSelectField("Category", "category", categories, formData.category)}

                  <MDBox mb={2}>
                    <MDInput
                      type="file"
                      name="uploaded_files"
                      variant="standard"
                      inputProps={{ multiple: true }}
                      fullWidth
                      onChange={(e) => handleChange(e)}
                    />
                  </MDBox>
                  <br></br>
                  <br></br>
                  {/* Message Texts */}
                  {formData.message_texts.map((message, index) => (
                    <div key={index}>
                      <MDBox mb={2}>
                        <MDInput
                          type="text"
                          label="Message Text"
                          name="text"
                          value={message.text}
                          variant="standard"
                          fullWidth
                          error={!formData.message_texts[index].text}
                          onChange={(e) => handleChange(e, index)}
                        />
                      </MDBox>
                      <MDBox mb={2}>
                        <MDInput
                          type="number"
                          label="Deal Price"
                          name="deal_price"
                          value={message.deal_price}
                          variant="standard"
                          fullWidth
                          error={!formData.message_texts[index].deal_price}
                          onChange={(e) => handleChange(e, index)}
                        />
                      </MDBox>
                      <MDBox mb={2}>
                        <MDInput
                          type="number"
                          label="Discount (%)"
                          name="discount"
                          value={message.discount}
                          variant="standard"
                          fullWidth
                          error={!formData.message_texts[index].discount}
                          onChange={(e) => handleChange(e, index)}
                        />
                      </MDBox>
                      <MDBox mb={2}>
                        <MDInput
                          type="url"
                          label="Link"
                          name="link"
                          value={message.link}
                          variant="standard"
                          fullWidth
                          error={!formData.message_texts[index].link}
                          onChange={(e) => handleChange(e, index)}
                        />
                      </MDBox>
                      <MDBox mb={2}>
                        <MDInput
                          type="text"
                          label="Currency"
                          name="currency"
                          value={message.currency}
                          variant="standard"
                          fullWidth
                          error={!formData.message_texts[index].currency}
                          onChange={(e) => handleChange(e, index)}
                        />
                      </MDBox>
                      <MDBox mb={2}>
                        <MDInput
                          type="text"
                          label="Sub ID"
                          name="sub_id"
                          value={message.sub_id}
                          variant="standard"
                          fullWidth
                          error={!formData.message_texts[index].sub_id}
                          onChange={(e) => handleChange(e, index)}
                        />
                      </MDBox>
                      <MDBox mb={2}>
                        <MDButton
                          variant="text"
                          type="button"
                          color="error"
                          onClick={() => handleRemoveMessage(index)}
                        >
                          Remove Message
                        </MDButton>
                      </MDBox>
                      <hr></hr>
                      <br></br>
                    </div>
                  ))}

                  <MDBox mb={2}>
                    <Button variant="text" color="primary" onClick={handleAddMessage}>
                      Add More Message
                    </Button>
                  </MDBox>

                  <MDBox mb={2}>
                    <MDButton color="info" fullWidth type="submit" disabled={isSubmitting}>
                      {isSubmitting ? <CircularProgress size={24} /> : "Create Multiple Message"}
                    </MDButton>
                  </MDBox>
                </MDBox>
              </MDBox>
            </Card>
          </MDBox>
        </Grid>
      </Grid>
      <Footer />
    </DashboardLayout>
  );
};

export default MultipleMessage;
